import React, { useCallback, useEffect, useRef, useState } from 'react';
import ProspectingSearchesPanel from './ProspectingSearchesPanel';
import ProspectingTopBar from './ProspectingTopBar';
import ProspectingPreviewPanel from './ProspectingPreviewPanel';
import ProspectingAssistantPanel from './ProspectingAssistantPanel';
import ProspectingSavedListTable from './ProspectingSavedListTable';
import { useProspecting } from '../../context/ProspectingContext';
import { useToast } from '../../context/ToastContext';
import {
  PROSPECTING_SAVED_LIST_LIMIT,
  buildLeadPreviewRequest,
  createProspectingSavedList,
  loadProspectingSavedLists,
} from './prospectingApi';

export default function SimpleModeWorkspace({ search }) {
  const { showError, showSuccess } = useToast();
  const { markTableIntentHandled } = useProspecting();
  const [searchesCollapsed, setSearchesCollapsed] = useState(false);
  const [savedLists, setSavedLists] = useState([]);
  const [savedListsLoading, setSavedListsLoading] = useState(true);
  const [savedListsReloadToken, setSavedListsReloadToken] = useState(0);
  const [activeSavedListId, setActiveSavedListId] = useState(null);
  const [savedListReloadToken, setSavedListReloadToken] = useState(0);
  const [savingList, setSavingList] = useState(false);
  const [assistantCollapsed, setAssistantCollapsed] = useState(false);
  const [latestPreviewResponse, setLatestPreviewResponse] = useState(null);
  const tableIntentStartedRef = useRef(new Set());

  const reloadSavedLists = useCallback(() => {
    setSavedListsReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setSavedListsLoading(true);

    loadProspectingSavedLists({ signal: controller.signal })
      .then((lists) => {
        setSavedLists(lists);
        setSavedListsLoading(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setSavedListsLoading(false);
        showError(error.message || 'Could not load saved lists');
      });

    return () => controller.abort();
  }, [savedListsReloadToken, showError]);

  useEffect(() => {
    setActiveSavedListId(null);
    setLatestPreviewResponse(null);
  }, [search.id]);

  const handleShowResults = useCallback(() => {
    setActiveSavedListId(null);
  }, []);

  const handleOpenSavedList = useCallback((listId) => {
    setActiveSavedListId(listId);
    setSavedListReloadToken((value) => value + 1);
  }, []);

  const handleSaveList = useCallback(async () => {
    if (savingList) return;
    setSavingList(true);
    try {
      const request = buildLeadPreviewRequest(search, PROSPECTING_SAVED_LIST_LIMIT);
      const savedList = await createProspectingSavedList({
        name: search.name,
        request,
        previewResponse: latestPreviewResponse,
      });
      setActiveSavedListId(savedList.id);
      setSavedListReloadToken((value) => value + 1);
      reloadSavedLists();
      showSuccess('List saved');
    } catch (error) {
      showError(error.message || 'Could not save list');
    } finally {
      setSavingList(false);
    }
  }, [latestPreviewResponse, reloadSavedLists, savingList, search, showError, showSuccess]);

  useEffect(() => {
    if (!search.openTableOnStart || activeSavedListId) return;
    if (tableIntentStartedRef.current.has(search.id)) return;

    tableIntentStartedRef.current.add(search.id);
    markTableIntentHandled(search.id);
    handleSaveList();
  }, [activeSavedListId, handleSaveList, markTableIntentHandled, search.id, search.openTableOnStart]);

  return (
    <>
      <ProspectingTopBar
        search={search}
        onSaveList={handleSaveList}
        savingList={savingList}
        savedListActive={Boolean(activeSavedListId)}
        onShowResults={handleShowResults}
      />
      <div className="flex-1 flex overflow-hidden">
        {!activeSavedListId && (
          <ProspectingSearchesPanel
            activeSearchId={search.id}
            collapsed={searchesCollapsed}
            onToggle={() => setSearchesCollapsed((v) => !v)}
            savedLists={savedLists}
            savedListsLoading={savedListsLoading}
            activeSavedListId={activeSavedListId}
            onOpenSavedList={handleOpenSavedList}
            onShowResults={handleShowResults}
          />
        )}
        {activeSavedListId ? (
          <ProspectingSavedListTable
            listId={activeSavedListId}
            reloadToken={savedListReloadToken}
            onReloadSavedLists={reloadSavedLists}
          />
        ) : (
          <ProspectingPreviewPanel
            search={search}
            wideGrid={searchesCollapsed}
            onBuildList={handleSaveList}
            savingList={savingList}
            onPreviewResponse={setLatestPreviewResponse}
          />
        )}
        <ProspectingAssistantPanel
          search={search}
          collapsed={assistantCollapsed}
          onToggle={() => setAssistantCollapsed((value) => !value)}
        />
      </div>
    </>
  );
}
